import React from "react";
import { resetPassword } from "../../../shared/functions";
import {
  Type,
  Anchor,
  Button,
  Field,
  Input,
  Alert,
} from "discover.medical.react-component-library";
import Title from "../title";
import Captcha from "../../captcha";
import "./index.css";
import { logSignInViewed, logSignInClicked } from "../../../shared/analytics";

// Fix is from https://stackoverflow.com/questions/48678359/ios-11-safari-html-disable-smart-punctuation
const createConversionMap = () => {
  var map = {};
  // Open-quotes: http://www.fileformat.info/info/unicode/category/Pi/list.htm
  map[0x2018] = "'";
  map[0x201b] = "'";
  map[0x201c] = '"';
  map[0x201f] = '"';

  // Close-quotes: http://www.fileformat.info/info/unicode/category/Pf/list.htm
  map[0x2019] = "'";
  map[0x201d] = '"';

  // Primes: http://www.fileformat.info/info/unicode/category/Po/list.htm
  map[0x2032] = "'";
  map[0x2033] = '"';
  map[0x2035] = "'";
  map[0x2036] = '"';

  map[0x2014] = "-"; // iOS 11 also replaces dashes with em-dash
  map[0x2013] = "-"; // and "--" with en-dash

  return map;
};

class LoginForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      username: props.username,
      password: "",
      error: null,
      isContinueVisible: false,
      isSubmitDisabled: false,
      captcha: null,
      SSOConnections: [],
      SSOConnectionsError: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.captchaRef = React.createRef();

    this.conversionMap = createConversionMap();
  }

  componentDidMount() {
    logSignInViewed();

    if (this.props.webAuth && this.captchaRef.current) {
      this.setState({
        captcha: this.props.webAuth.renderCaptcha(this.captchaRef.current),
      });
    }
  }

  getErrorMessage() {
    return (
      this.state.error.description ||
      this.state.error.message ||
      "We're sorry, something went wrong. Please try again."
    );
  }

  handleChange = (e) => {
    const currentString = e.target.value;
    let newValue = "";
    
    if (typeof currentString === "string") {
      for(let i=0; i<currentString.length; i++) {
        const conversionChar = this.conversionMap[currentString.charCodeAt(i)];
  
        if (conversionChar) {
          newValue += conversionChar;
        } else {
          newValue += currentString[i];
        }
      }

      this.setState({ [e.target.name]: newValue });
    } else {
      this.setState({ [e.target.name]: e.target.value });
    }
  };

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }
    const { webAuth, databaseConnection, contextToken } = this.props;
    const { username, password } = this.state;

    this.setState({ isSubmitDisabled: true });

    if (username !== "" && password !== "") {
      // Do nothing if there is no webAuth obj
      if (!webAuth) {
        return;
      }
      // Proceed with login
      const login = contextToken ? `${contextToken}@@@${username}` : username;
      webAuth.login(
        {
          realm: databaseConnection,
          username: login,
          password: password,
          captcha: this.state.captcha.getValue(),
        },
        (err) => {
          this.setState({ isSubmitDisabled: false });
          if (err) {
            this.setState({ error: err });
            this.state.captcha.reload();
          }
        }
      );
    } else {
      this.setState({ isSubmitDisabled: false });
      this.setState({
        error: {
          description:
            "Your username and/or password is incorrect or missing. Please try again.",
        },
      });
    }

    logSignInClicked();
  }

  render() {
    const {
      onFormChange,
      fromSource,
      organizationContext,
      forgotPassword,
      successMessage,
      webAuth,
      eisSource,
    } = this.props;

    const { isSubmitDisabled, username, password } = this.state;
    let resetPasswordUrl = "accounts.ebsco.com";
    if (
      webAuth &&
      webAuth.baseOptions &&
      (webAuth.baseOptions.domain.includes("devqa") ||
        webAuth.baseOptions.domain.includes("integration"))
    ) {
      resetPasswordUrl = "staging-accounts.ebsco.com";
    }

    return (
      <div data-testid="login-form">
        <Title text="Sign in to your Personal Account">
          {(organizationContext || fromSource === "subscription") && (
            <>
              <Type
                element="i"
                color="gray700"
                fontSize="sm"
                display="inline-block"
                marginTop="sm"
              >
                Don't have a personal account?{" "}
                <Anchor
                  id="other-link"
                  href="#"
                  onClick={() => onFormChange("register")}
                >
                  Register Now
                </Anchor>
              </Type>
              {eisSource === "ehr" && (
                <Type
                  className="org_context_banner"
                  element="p"
                  fontSize="sm"
                  color="gray700"
                  marginY="sm"
                >
                  Signing in here will affiliate your account with{" "}
                  <Type element="strong" fontSize="sm">
                    {organizationContext}
                  </Type>
                  .
                </Type>
              )}
              {organizationContext && eisSource !== "ehr" && (
                <Type
                  className="org_context_banner"
                  element="p"
                  fontSize="sm"
                  color="gray700"
                  marginY="sm"
                >
                  Signing in here will affiliate your account with{" "}
                  <Type element="strong" fontSize="sm">
                    {organizationContext}
                  </Type>{" "}
                  and give you access to their subscribed content. Alternative
                  sign in options are below.
                </Type>
              )}
            </>
          )}
        </Title>

        <form id="login_form" onSubmit={this.handleSubmit}>
          {this.state.error && (
            <Alert className="Form__alert" type="error">
              {this.getErrorMessage()}
            </Alert>
          )}

          {!this.state.error && successMessage && (
            <Alert className="Form__alert" type="success">
              {successMessage}
            </Alert>
          )}

          <Field id="username" label="Email Address" className="Form_field">
            <Input.Text
              name="username"
              autoComplete="username"
              value={username}
              onChange={this.handleChange}
              disabled={isSubmitDisabled}
            />
          </Field>

          <Field id="password" label="Password" className="Form_field">
            <Input.Text
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={this.handleChange}
              disabled={isSubmitDisabled}
            />
          </Field>

          <Captcha className="Login__captcha" ref={this.captchaRef} />

          {forgotPassword && (
            <Anchor
              id="other-link"
              href="#"
              onClick={() => resetPassword(resetPasswordUrl)}
            >
              Forgot Password?
            </Anchor>
          )}

          <div className={"Login__button-wrap"}>
            <Button
              id="btn-login"
              className="Form__button"
              type="submit"
              disabled={isSubmitDisabled}
            >
              Sign In
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

export default LoginForm;
